import { useState } from "react";

import { Avatar } from "@/components/pieces/users/Avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IntegrationDeletionModal } from "@/components/ui/integration-deletion-modal";
import { cn } from "@/lib/utils";

interface IntegrationInstanceCardProps {
  name: string;
  integrationName: string;
  enabledBy: {
    name: string;
    avatar?: string;
  };
  enabledOn: string;
  onDisconnect?: () => void;
  loading?: boolean;
  className?: string;
}

function IntegrationInstanceCard({
  name,
  integrationName,
  enabledBy,
  enabledOn,
  onDisconnect,
  loading = false,
  className,
}: IntegrationInstanceCardProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDisconnectClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDisconnect = () => {
    onDisconnect?.();
    setShowDeleteModal(false);
  };

  const handleCancelDisconnect = () => {
    setShowDeleteModal(false);
  };

  return (
    <Card variant="plain" className={cn("w-full min-w-[600px]", className)}>
      <CardContent className="p-6">
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold leading-snug text-gray-800 m-0">
            {name}
          </h3>
          <div className="flex justify-between items-start gap-6">
            <div className="flex items-center gap-8 min-w-0 flex-1">
              <div className="flex items-center gap-4 min-w-0">
                <Avatar
                  user={{
                    name: enabledBy.name,
                    photo: enabledBy.avatar,
                  }}
                  size={40}
                />
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="text-base font-semibold leading-snug text-gray-800">
                    Enabled By
                  </div>
                  <div className="text-base font-normal leading-snug text-gray-800 truncate">
                    {enabledBy.name}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <div className="text-base font-semibold leading-snug text-gray-800">
                  Enabled On
                </div>
                <div className="text-base font-normal leading-snug text-gray-800">
                  {enabledOn}
                </div>
              </div>
            </div>
            {onDisconnect && (
              <Button
                intent="secondary"
                variant="solid"
                size="md"
                onClick={handleDisconnectClick}
                disabled={loading}
                className="shrink-0 px-4"
              >
                {loading ? "Disconnecting..." : "Disconnect"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>

      <IntegrationDeletionModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        integrationName={integrationName}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
        loading={loading}
      />
    </Card>
  );
}

export { IntegrationInstanceCard };
export type { IntegrationInstanceCardProps };
